import React, { useState, useEffect } from "react";
import { Calendar, FileText, User, Clock, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Alert, AlertDescription } from "../../../../components/ui/alert";

// Components
import Status from "components/ui/status";
import RequestFormParser from "../utils/RequestFormParser";
import { displayTableResults } from "./TableResults";

// Utils and Constants
import {
  INQUIRY_TYPE_TO_REQUEST_TYPE,
  ORIGINAL_INQUIRY,
  ELIGIBILITY_SEARCH_QUERIES_ALL,
} from "utils/constants";
import baseApi from "utils/axios-instance";
import { ParseTime } from "utils/parseTime";
import { validateEligibilityJson } from "utils/transforms/backend_validations";
import {
  transformEligibilityQueryResults,
  customDatesJsonStringParser,
} from "./Transforms";

interface RequestFormProps {
  requestDetails: any;
  patientName: string;
  subject: string;
  topic: string;
  status: string;
  time: string[];
  showRequestID: string;
  onPrintDataReady?: (data: {
    tableResults: any;
    eligibilitySearchJson: any;
  }) => void;
}

interface TableResults {
  sections: any[];
}

const RequestForm: React.FC<RequestFormProps> = ({
  requestDetails,
  patientName,
  subject,
  topic,
  status,
  time,
  showRequestID,
  onPrintDataReady,
}) => {
  const [eligibilitySearchJson, setEligibilitySearchJson] = useState<any>(null);
  const [tableResults, setTableResults] = useState<TableResults>({
    sections: [],
  });
  const [claimsDOS, setClaimsDOS] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Notify parent when print data is ready
  useEffect(() => {
    if (onPrintDataReady) {
      onPrintDataReady({
        tableResults,
        eligibilitySearchJson,
      });
    }
  }, [tableResults, eligibilitySearchJson, onPrintDataReady]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        if (topic === "ELIGIBILITY_SEARCH" && status === "SUCCESS") {
          const response = await baseApi.get(
            `/dashboard/data/eligibility_search/${showRequestID}`,
            { responseType: "text" }
          );

          const data = JSON.parse(response.data, customDatesJsonStringParser);
          if (data.eligibility_query_results) {
            setTableResults(
              transformEligibilityQueryResults(data.eligibility_query_results)
            );
          }

          // Fallback to legacy API if the new API fails
          if (tableResults.sections.length === 0) {
            const legacyResponse = await baseApi.get(
              `/dashboard/data/eligibility_search_legacy/${showRequestID}`
            );
            if (legacyResponse.data.eligibility_json) {
              validateEligibilityJson(legacyResponse.data.eligibility_json);
              setEligibilitySearchJson(legacyResponse.data.eligibility_json);
            }
          }
        }

        if (topic === "CLAIMS") {
          if (requestDetails?.claims_date_of_service) {
            setClaimsDOS(ParseTime(requestDetails.claims_date_of_service));
          }

          if (status === "SUCCESS") {
            const response = await baseApi.get(
              `/dashboard/data/claims_status/${showRequestID}`
            );
            if (response.data.claims_status_json) {
              validateEligibilityJson(response.data.claims_status_json);
              setEligibilitySearchJson(response.data.claims_status_json);
            }
          }
        }

        if (topic === "PRIOR_AUTH_STATUS" && status === "SUCCESS") {
          const response = await baseApi.get(
            `/dashboard/data/prior_auth_status/${showRequestID}`
          );
          if (response.data.prior_auth_status_json) {
            validateEligibilityJson(response.data.prior_auth_status_json);
            setEligibilitySearchJson(response.data.prior_auth_status_json);
          }
        }
      } catch (error) {
        console.error("Error fetching request data:", error);
        setError(
          "Failed to load request details. Please contact support if this issue persists."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    topic,
    status,
    showRequestID,
    requestDetails,
    tableResults.sections.length,
  ]);

  // Get eligibility search question labels
  const getEligibilityQuestionLabels = () => {
    if (!requestDetails?.eligibility_search_queries?.length) return "";

    const labels: string[] = [];
    requestDetails.eligibility_search_queries.forEach((searchQuery: string) => {
      const found = ELIGIBILITY_SEARCH_QUERIES_ALL.find(
        (item) => item.value === searchQuery
      );
      if (found) {
        labels.push(found.label);
      }
    });
    return labels.join(", ");
  };

  const renderRequestOverview = () => (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[#005151]/10 rounded-lg">
                <FileText className="h-5 w-5 text-[#005151]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {INQUIRY_TYPE_TO_REQUEST_TYPE[topic] || topic}
                </h3>
                <p className="text-sm text-gray-600">{subject}</p>
              </div>
            </div>
          </div>
          <Status status={status} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Patient</p>
              <p className="text-sm text-gray-600">{patientName}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Calendar className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Created</p>
              <p className="text-sm text-gray-600">{time.join(", ")}</p>
            </div>
          </div>

          {topic === "CLAIMS" && claimsDOS && (
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Clock className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Date of Service
                </p>
                <p className="text-sm text-gray-600">
                  {claimsDOS[0]}, {claimsDOS[1]}
                </p>
              </div>
            </div>
          )}

          {topic === "ELIGIBILITY_SEARCH" && getEligibilityQuestionLabels() && (
            <div className="md:col-span-3">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <FileText className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Search Queries
                  </p>
                  <p className="text-sm text-gray-600">
                    {getEligibilityQuestionLabels()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderRequestDetails = () => {
    if (!requestDetails || !ORIGINAL_INQUIRY?.length) return null;

    const validFields = ORIGINAL_INQUIRY.filter((item) => {
      const fieldValue = requestDetails[item.key];
      if (typeof fieldValue === "boolean") {
        return true;
      }
      return fieldValue != null && fieldValue !== "";
    });

    if (validFields.length === 0) return null;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Request Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {validFields.map((item) => (
              <div key={item.key} className="py-3">
                <p className="text-sm font-medium text-gray-500">
                  {item.value}
                </p>
                <div className="text-sm text-gray-900 mt-1">
                  {typeof requestDetails[item.key] === "boolean" ? (
                    requestDetails[item.key] ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : null
                  ) : (
                    requestDetails[item.key]
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (error) {
    return (
      <Alert className="mb-6">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {renderRequestOverview()}

      {renderRequestDetails()}

      {loading && (
        <Card>
          <CardContent className="py-8">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#005151]"></div>
              <span className="text-gray-600">Loading request data...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && (
        <>
          {displayTableResults(tableResults)}
          {eligibilitySearchJson && (
            <RequestFormParser json={eligibilitySearchJson} />
          )}
        </>
      )}
    </div>
  );
};

export default RequestForm;
